import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import api, { mediaUrl, kr } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export function CartDrawer() {
  const navigate = useNavigate();
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartSubtotal, settings, oneMore } = useApp();
  const [upsell, setUpsell] = useState([]);
  const [ev, setEv] = useState(null);
  const sig = cart.map((i) => `${i.id}:${i.quantity}`).join(",");
  useEffect(() => { if (cartOpen && cart.length) api.post("/campaigns/evaluate", { items: cart.map((i) => ({ product_id: i.id, quantity: i.quantity })) }).then(({ data }) => setEv(data)).catch(() => setEv(null)); else setEv(null); }, [cartOpen, sig]); // eslint-disable-line
  const ids = cart.map((i) => i.id).join(",");
  useEffect(() => { if (cartOpen) api.post("/recommendations/cart", { product_ids: ids ? ids.split(",") : [], limit: 5 }).then(({ data }) => setUpsell(data)).catch(() => {}); }, [cartOpen, ids]);
  const threshold = settings?.free_shipping_threshold ?? 300;
  const remaining = Math.max(0, threshold - cartSubtotal);
  const pct = Math.min(100, (cartSubtotal / threshold) * 100);

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setCartOpen(false)} className="fixed inset-0 z-[60] bg-black/60" />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween" }}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[60] flex h-full w-full max-w-md flex-col border-l border-border bg-background"
          >
            <div className="flex items-center justify-between border-b border-border p-5">
              <h3 className="flex items-center gap-2 text-lg font-bold"><ShoppingBag className="h-5 w-5 text-primary" /> Din kurv</h3>
              <button onClick={() => setCartOpen(false)} data-testid="cart-close"><X className="h-6 w-6" /></button>
            </div>

            {cart.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
                <ShoppingBag className="h-12 w-12 opacity-40" />
                <p>Din kurv er tom</p>
                <button onClick={() => { setCartOpen(false); navigate("/produkter"); }} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Shop nu</button>
              </div>
            ) : (
              <>
                <div className="border-b border-border p-4">
                  {remaining > 0 ? (
                    <p className="mb-2 text-xs text-muted-foreground" data-testid="free-shipping-msg">Du mangler kun <span className="font-bold text-primary">{kr(remaining)}</span> for <span className="font-bold">fri fragt</span></p>
                  ) : (
                    <p className="mb-2 text-xs font-bold text-candy-green" data-testid="free-shipping-msg">🎉 Du har opnået fri fragt!</p>
                  )}
                  <div className="h-2 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>

                <div className="flex-1 overflow-auto p-4">
                  {cart.map((item) => (
                    <div key={item.id} data-testid={`cart-item-${item.id}`} className="mb-3 flex gap-3 rounded-xl border border-border bg-card p-3">
                      <img src={mediaUrl(item.image)} alt={item.name} className="h-16 w-16 rounded-lg object-cover" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold leading-tight">{item.name}</p>
                        <p className="text-sm font-bold text-primary">{kr(item.price)}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <button onClick={() => updateQty(item.id, item.quantity - 1)} className="flex h-6 w-6 items-center justify-center rounded-full border border-border hover:bg-secondary"><Minus className="h-3 w-3" /></button>
                          <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                          <button onClick={() => updateQty(item.id, Math.min(item.quantity + 1, item.stock))} className="flex h-6 w-6 items-center justify-center rounded-full border border-border hover:bg-secondary"><Plus className="h-3 w-3" /></button>
                          <button onClick={() => removeFromCart(item.id)} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {upsell.length > 0 && (
                    <div className="mt-4" data-testid="cart-upsell">
                      <p className="mb-2 text-sm font-black">Skal der lige 1 mere med? 🍬</p>
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {upsell.map((p) => (
                          <div key={p.id} className="w-28 shrink-0 rounded-xl border border-border bg-card p-2" data-testid={`upsell-${p.slug}`}>
                            <img src={mediaUrl(p.main_image)} alt={p.name} className="mb-1 h-16 w-full rounded-lg object-cover" />
                            <p className="line-clamp-2 text-[11px] font-semibold leading-tight">{p.name}</p>
                            <p className="text-[11px] font-bold text-primary">{kr(p.effective_price)}</p>
                            <button onClick={(e) => oneMore(p, e)} data-testid={`upsell-add-${p.slug}`} className="gloss mt-1 w-full rounded-full bg-foreground py-1 text-[10px] font-black text-background">+ 1 MERE</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-border p-5">
                  {ev?.discounts?.map((d) => <div key={d.campaign_id} className="mb-1 flex items-center justify-between text-sm text-candy-green" data-testid="cart-discount"><span>🎉 {d.name}</span><span className="font-bold">-{kr(d.amount)}</span></div>)}
                  {ev?.free_products?.map((f) => <div key={f.product_id} className="mb-1 text-sm text-candy-green">🎁 Gratis: {f.name} ({f.campaign})</div>)}
                  {ev?.free_shipping && <div className="mb-1 text-sm text-candy-green">🚚 Gratis fragt (kampagne)</div>}
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{ev?.total_discount ? "Subtotal efter rabat" : "Subtotal"}</span>
                    <span className="text-lg font-bold" data-testid="cart-subtotal">{kr(cartSubtotal - (ev?.total_discount || 0))}</span>
                  </div>
                  <button
                    data-testid="cart-checkout-btn"
                    onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                    className="gloss w-full rounded-full bg-primary py-3.5 font-bold text-primary-foreground transition-transform hover:scale-[1.02]"
                  >
                    Gå til checkout
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
